import { ReactNode } from "react";

function DocIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

export function Rag() {
  return (
    <article className="project-card featured">
      <div className="featured-text">
        <div className="tags">
          <span className="tag tag-new">NEW · 2026</span>
          <span className="tag">Generative AI · RAG</span>
        </div>
        <h3>RAG</h3>
        <p>
          A Retrieval-Augmented Generation assistant that answers questions over company documents
          in natural language — every answer grounded in, and linked to, its sources.
        </p>
      </div>
      {/* Illustrative conversation, not real client data */}
      <div className="chat" aria-label="Example conversation with the RAG assistant">
        <div className="chat-status">
          <span className="dot" />
          assistant · connected to knowledge base
        </div>
        <div className="chat-question">What's the notice period in our supplier contracts?</div>
        <div className="chat-sources">
          <span className="chat-label">RETRIEVED · 3 sources</span>
          <div>
            <span className="source"><DocIcon />Contract_A.pdf · p.4</span>
            <span className="source"><DocIcon />Policy_2025.docx</span>
            <span className="source"><DocIcon />FAQ_legal.md</span>
          </div>
        </div>
        <div className="chat-answer">
          Standard supplier contracts require <strong>60 days' written notice</strong>{" "}
          <span className="cite">[1]</span>, reduced to 30 days for contracts under 12 months{" "}
          <span className="cite">[2]</span>.
        </div>
        <div className="chat-input">
          <span>Ask anything about your documents…</span>
          <span className="send">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 19V5M6 11l6-6 6 6" />
            </svg>
          </span>
        </div>
      </div>
    </article>
  );
}

export interface ProjectProps {
  media: ReactNode;
  meta: string;
  title: string;
  description: ReactNode;
}

export function Project(props: ProjectProps) {
  return (
    <article className="project-card">
      <div className="project-media">{props.media}</div>
      <div className="project-body">
        <span className="project-meta">{props.meta}</span>
        <h3>{props.title}</h3>
        <p>{props.description}</p>
      </div>
    </article>
  );
}

const img = (file: string, alt: string) => (
  <img src={process.env.PUBLIC_URL + "/projects-img/" + file} alt={alt} />
);

export function ProjectsSection() {
  return (
    <section className="projects" id="work">
      <div className="section-header">
        <div>
          <span className="eyebrow">01 — SELECTED WORK</span>
          <h2>Products we've shipped</h2>
        </div>
        <p>From AI assistants to SaaS for investment funds — each one designed, built and run by us.</p>
      </div>
      <div className="bento">
        <Rag />
        <Project
          media={
            <video
              src="https://cdn.prod.website-files.com/64db7019c9987712d282ba00/655de7cc4796003d4b302bec_Swiftcell%20-%20Video%201%20v7-transcode.mp4"
              autoPlay
              loop
              muted
              playsInline
            />
          }
          meta="FINTECH · SAAS · 2023"
          title="DealBase"
          description={
            <>
              <a href="https://www.dealbase.co/">Excel plug-in and web app</a> standardising SaaS KPI
              analyses — cohort retention, TTM, benchmarks. Used by leading international
              investment funds.
            </>
          }
        />
        <Project
          media={<div className="phone-media">{img("byTheWake-phone.png", "ByTheWake booking screen")}</div>}
          meta="SPORT · MOBILE · 2023"
          title="ByTheWake"
          description={
            <>
              <a href="https://www.bythewake.com">Booking, payments and progress tracking</a> for
              riders; a full management suite for wake parks. 10 parks, 20,000+ users.
            </>
          }
        />
        <Project
          media={img("coffeeApp.png", "Tablet sales and logistics app")}
          meta="LOGISTICS · TABLET"
          title="Administration & logistics"
          description="Offline-first tablet app covering the full sales workflow, from warehouse to invoice, synced with accounting and printing on Zebra® devices."
        />
        <Project
          media={img("airlineMonitor.svg", "Airline pricing monitor dashboard")}
          meta="DATA · RESEARCH"
          title="Airline Pricing Intelligence"
          description="Proxy network and statistical analysis of 4M+ data points to test whether European airlines price dynamically on returning visitors."
        />
      </div>
    </section>
  );
}
